import React, { useState, useEffect } from 'react';
import { FiList, FiRefreshCw, FiArrowUp, FiArrowDown, FiChevronLeft, FiChevronRight, FiShare2, FiInfo } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { tradesApi } from '../../api';
import { useAuthStore } from '../../stores';
import { Loader } from '../Common/Loader';
import ShareOrderModal from './ShareOrderModal';
import { Trade } from '../../types';
import { formatTradeErrorForDisplay } from '../../utils/formatTradeError';
import { formatSignedUsd, formatUsd } from '../../utils/formatUsd';

interface OrdersListProps {
  selectedBrokerId?: string | null;
}

const isWebullConnection = (conn?: { brokerageSlug?: string; brokerageName?: string; brokerageId?: string }) => {
  if (!conn) return false;
  const slug = String(conn.brokerageSlug || '').toLowerCase();
  const name = String(conn.brokerageName || '').toLowerCase();
  const id = String(conn.brokerageId || '').toLowerCase();
  return slug.includes('webull') || name.includes('webull') || id.includes('webull');
};

const OrdersList: React.FC<OrdersListProps> = ({ selectedBrokerId }) => {
  const { user } = useAuthStore();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 5;
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>(selectedBrokerId || '');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareOrder, setShareOrder] = useState<Trade | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (selectedBrokerId !== undefined && selectedBrokerId !== selectedConnectionId) {
      setSelectedConnectionId(selectedBrokerId || '');
      setCurrentPage(1);
    }
  }, [selectedBrokerId, selectedConnectionId]);

  const { data: connectionsData } = useApi.useBrokerageConnections({
    enabled: !!user?._id,
    refetchOnWindowFocus: false,
  });

  const activeConnections = (connectionsData?.connections || []).filter((c: { status: string }) => c.status === 'active');
  const selectedConnection = activeConnections.find((c: { _id: string }) => c._id === selectedConnectionId);
  const isWebullSelected = !!selectedConnectionId && isWebullConnection(selectedConnection);

  const { data: tradesData, isLoading, refetch, isFetching } = useApi.useTrades(
    {
      page: currentPage,
      limit: itemsPerPage,
      status: statusFilter === 'all' ? undefined : statusFilter,
      brokerId: selectedConnectionId || undefined,
    },
    {
      enabled: !!user?._id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 1000,
    }
  );

  const trades = tradesData?.trades || [];
  const totalTrades = tradesData?.total || 0;
  const totalPages = tradesData?.totalPages || 1;
  const shouldShowPagination = totalPages > 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, selectedConnectionId]);

  useEffect(() => {
    let socket: ReturnType<typeof import('../../services/socketService').socketService.getSocket> = null;
    let cleanup: (() => void) | null = null;

    import('../../services/socketService').then(({ socketService }) => {
      socket = socketService.getSocket();
      if (!socket) return;

      const triggerRefetch = () => setTimeout(() => refetch(), 250);

      socket.on('trade:new', triggerRefetch);
      socket.on('trade:status', triggerRefetch);

      cleanup = () => {
        if (!socket) return;
        socket.off('trade:new', triggerRefetch);
        socket.off('trade:status', triggerRefetch);
      };
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [refetch]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
    } catch {
      return 'N/A';
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing || isFetching) return;
    setIsRefreshing(true);
    try {
      if (isWebullSelected && selectedConnectionId) {
        await tradesApi.syncWebullLast24(selectedConnectionId);
      }
      await refetch();
    } catch (err) {
      console.error('Failed to refresh trades:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'filled') return 'badge-success';
    if (['pending', 'new', 'submitted', 'pending_new'].includes(statusLower)) return 'badge-warning';
    if (['partial', 'partially_filled'].includes(statusLower)) return 'bg-info/10 text-info border-info/30';
    if (['cancelled', 'canceled', 'expired', 'rejected', 'failed'].includes(statusLower)) return 'badge-danger';
    return 'badge-warning';
  };

  return (
    <>
      <div className="space-y-4 sm:space-y-8 animate-fade-in overflow-x-hidden">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="hidden sm:flex items-center gap-3" />
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 w-full sm:flex sm:gap-3 sm:w-auto">
            <select
              value={selectedConnectionId}
              onChange={(e) => setSelectedConnectionId(e.target.value)}
              className="w-full sm:w-auto min-w-0 h-10 sm:h-auto px-3 sm:px-4 py-2 text-sm sm:text-base truncate bg-surface border-2 border-border rounded-lg text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
            >
              <option value="">All Accounts</option>
              {activeConnections.map((c: { _id: string; brokerageName?: string; brokerageId?: string }) => (
                <option key={c._id} value={c._id}>
                  {c.brokerageName || c.brokerageId}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-auto min-w-0 h-10 sm:h-auto px-3 sm:px-4 py-2 text-sm sm:text-base truncate bg-surface border-2 border-border rounded-lg text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
            >
              <option value="all">All Status</option>
              <option value="filled">Filled</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed/Rejected</option>
            </select>
            <button
              className="btn-ghost flex h-10 w-10 sm:h-auto sm:w-auto shrink-0 items-center justify-center gap-2 rounded-lg border-2 border-border bg-surface sm:border-0 sm:bg-transparent p-0 sm:px-6 sm:py-2.5"
              onClick={handleRefresh}
              disabled={isFetching || isRefreshing}
              aria-label="Refresh trades"
            >
              {isFetching || isRefreshing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary" />
              ) : (
                <FiRefreshCw className="h-5 w-5" />
              )}
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {isLoading && trades.length === 0 ? (
          <div className="card-premium flex flex-col items-center justify-center h-64">
            <Loader message="Loading history..." />
          </div>
        ) : trades.length > 0 ? (
          <div className="card-premium overflow-hidden p-0 w-full">
            <div className="table-scroll overflow-x-auto">
              <table className="table-modern" style={{ minWidth: '1400px', width: '100%' }}>
                <thead>
                  <tr>
                    <th className="px-6 py-4 text-left">Asset</th>
                    <th className="px-6 py-4 text-left">Broker</th>
                    <th className="px-6 py-4 text-left">Side</th>
                    <th className="px-6 py-4 text-right">Qty</th>
                    <th className="px-6 py-4 text-right">Full Fill Price</th>
                    <th className="px-6 py-4 text-right">P&L ($)</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Submitted At</th>
                    <th className="px-6 py-4 text-left">Filled At</th>
                    <th className="px-6 py-4 text-center">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((trade, index) => {
                    const isBuy = trade.side.includes('buy');
                    const isPending = ['pending', 'new', 'submitted', 'pending_new'].includes(trade.status.toLowerCase());
                    const pnl = trade.profitLoss || 0;
                    const pnlClass = pnl > 0 ? 'text-success-500' : pnl < 0 ? 'text-danger-500' : 'text-muted';

                    return (
                      <tr
                        key={trade._id}
                        className={`hover:bg-surface transition-all duration-300 animate-fade-in ${isPending
                          ? 'border-l-4 border-yellow-500 bg-yellow-50/30 hover:bg-yellow-50/50 dark:bg-yellow-900/10 dark:hover:bg-yellow-900/20'
                          : isBuy
                            ? 'border-l-4 border-success-500 bg-success-50/30 hover:bg-success-50/50 dark:bg-success-900/10 dark:hover:bg-success-900/20'
                            : 'border-l-4 border-danger-500 bg-danger-50/30 hover:bg-danger-50/50 dark:bg-danger-900/10 dark:hover:bg-danger-900/20'
                          }`}
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <td className="px-6 py-4 font-bold text-lg text-foreground">
                          <div>{trade.symbol}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-surface-elevated rounded border border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            {trade.brokerName || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm ${isBuy
                            ? 'bg-gradient-to-r from-success-500 to-success-600 text-white'
                            : 'bg-gradient-to-r from-danger-500 to-danger-600 text-white'
                            }`}>
                            {isBuy ? <FiArrowUp className="h-3.5 w-3.5" /> : <FiArrowDown className="h-3.5 w-3.5" />}
                            {trade.side.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold tabular-nums">{(trade.qty || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right font-semibold tabular-nums">
                          {trade.filledPrice
                            ? formatUsd(trade.filledPrice)
                            : trade.limitPrice
                              ? formatUsd(trade.limitPrice)
                              : '-'}
                        </td>
                        <td className={`px-6 py-4 text-right font-bold tabular-nums ${pnlClass}`}>
                          {formatSignedUsd(pnl)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`badge ${getStatusBadgeClass(trade.status)}`}>{trade.status.toUpperCase()}</span>
                            {['failed', 'rejected'].includes(trade.status?.toLowerCase()) && (
                              <span
                                className="inline-flex text-danger-500 cursor-help"
                                title={formatTradeErrorForDisplay(trade.error || trade.notes)}
                              >
                                <FiInfo className="h-4 w-4" aria-label="Why this trade failed" />
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-muted">{formatDate(trade.createdAt)}</td>
                        <td className="px-6 py-4 text-xs text-muted">{formatDate(trade.filledAt || null)}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => { setShareOrder(trade); setShowShareModal(true); }}
                            className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-primary shadow-sm"
                            title="Share this order"
                          >
                            <FiShare2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-4 sm:px-6 py-4 bg-surface border-t-2 border-border flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm font-medium text-foreground">
                Showing <span className="text-primary">{(currentPage - 1) * itemsPerPage + 1}</span> - <span className="text-primary">{Math.min(currentPage * itemsPerPage, totalTrades)}</span> of <span className="text-primary">{totalTrades}</span> trades
                {isWebullSelected && (
                  <span className="text-muted ml-2">(Webull history)</span>
                )}
              </div>

              {shouldShowPagination && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1 || isFetching}
                    className="btn-ghost flex items-center gap-2 px-3 py-2 disabled:opacity-50"
                  >
                    <FiChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Previous</span>
                  </button>
                  <span className="px-4 py-2 bg-surface-elevated rounded-lg text-sm font-bold text-primary">
                    {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages || isFetching}
                    className="btn-ghost flex items-center gap-2 px-3 py-2 disabled:opacity-50"
                  >
                    <span className="hidden sm:inline">Next</span> <FiChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card-premium text-center py-16">
            <div className="bg-info/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiList className="h-10 w-10 text-info" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">No Trades Found</h3>
            <p className="text-muted text-base sm:text-lg mb-4">You don&apos;t have any trades matching these filters</p>
          </div>
        )}
      </div>

      {showShareModal && shareOrder && (
        <ShareOrderModal
          order={{
            id: shareOrder._id,
            symbol: shareOrder.symbol,
            side: shareOrder.side,
            qty: shareOrder.qty,
            filled_qty: shareOrder.qty,
            type: shareOrder.type,
            status: shareOrder.status,
            filled_avg_price: shareOrder.filledPrice || shareOrder.limitPrice,
            submittedAt: shareOrder.createdAt,
            pnl: shareOrder.profitLoss,
            pnl_percent: shareOrder.profitLossPercent,
            created_at: shareOrder.createdAt
          } as any}
          onClose={() => { setShowShareModal(false); setShareOrder(null); }}
        />
      )}
    </>
  );
};

export default OrdersList;
